import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Plus, Trash2, Heart, Shield, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Btn, ConfirmButton } from "@/components/common";
import CharacterWizard from "@/components/CharacterWizard";

export default function Characters() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: ref } = useQuery({ queryKey: ["reference"], queryFn: async () => (await api.get("/meta/reference")).data });
  const { data: chars, isLoading } = useQuery({
    queryKey: ["characters"],
    queryFn: async () => (await api.get("/characters")).data,
  });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/characters", payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["characters"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      toast.success("Character created!");
      if (data?.id) navigate(`/characters/${data.id}`);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/characters/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["characters"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Character deleted");
    },
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader
        title="Characters"
        subtitle="Your roster of heroes and villains"
        icon={Users}
        action={
          <Btn data-testid="new-character-btn" onClick={() => setOpen(true)}>
            <Plus size={16} /> New Character
          </Btn>
        }
      />

      {chars?.length ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {chars.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="glass glass-hover rounded-3xl overflow-hidden group relative"
              data-testid={`character-card-${c.id}`}
            >
              <Link to={`/characters/${c.id}`} className="block p-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display text-xl font-bold">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg font-semibold text-white truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500">Lvl {c.level} · {c.race} {c.class}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Stat icon={Heart} label="HP" value={`${c.hp?.current ?? 0}/${c.hp?.max ?? 0}`} color="text-rose-400" />
                  <Stat icon={Shield} label="AC" value={c.armor_class} color="text-sky-400" />
                  <Stat icon={Zap} label="Spd" value={c.speed} color="text-amber-400" />
                </div>
              </Link>
              <ConfirmButton
                onConfirm={() => del.mutate(c.id)}
                title="Delete character?"
                message={`"${c.name}" and all their stats will be permanently deleted.`}
                data-testid={`delete-char-${c.id}`}
                className="absolute top-4 right-4 p-2 rounded-lg text-zinc-600 hover:text-rose-400 hover:bg-rose-500/10 opacity-0 group-hover:opacity-100 transition"
              >
                <Trash2 size={16} />
              </ConfirmButton>
            </motion.div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Users}
          title="No characters yet"
          subtitle="Create your first hero to start tracking stats, spells and inventory."
          action={<Btn onClick={() => setOpen(true)}><Plus size={16} /> Create Character</Btn>}
        />
      )}

      <CharacterWizard
        open={open}
        onClose={() => setOpen(false)}
        reference={ref}
        onCreate={(payload) => create.mutate(payload)}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/[0.05] py-2 flex flex-col items-center">
      <Icon size={14} className={color} />
      <div className="text-sm font-semibold text-white mt-1">{value}</div>
      <div className="text-[10px] text-zinc-600 uppercase">{label}</div>
    </div>
  );
}
